import * as urlBuilder from './urls'
import { makeServiceBuilder } from '@/common/use-http'
import { AcaoPermitidaParaPlanoEscolhidoEnum, ConfirmOTPResponse } from '../../http/dto/ConfirmOTPResponse'
import { NotifyUserActionRequest } from '../../http/dto/NotifyUserActionRequest'
import { getChannel } from '../../offers/store'
import { CpfDivergenteOtpGeradoException } from '../../exception/CpfDivergenteOtpGeradoExceptionException'

/**
 * confirm client otp
 *
 * @param {HttpClient} http
 * @param {String} msisdn
 * @param {String} action
 *
 * @returns {Promise<Void>}
 */
const notifyUserAction = async (http, msisdn, action, extra) => {
  if (msisdn) {
    await http.post(
      urlBuilder.NOTIFY_USER_ACTION(msisdn),
      new NotifyUserActionRequest(action, extra),
      false
    )
  }
}

/**
 * confirm client otp
 *
 * @param {HttpClient} http
 * @param {String} msisdn
 * @param {String} action
 *
 * @returns {Promise<Void>}
 */
const notifyUserActionWithTelevendasId = async (http, msisdn, action, extra, televendasId) => {
  if (msisdn) {
    await http.post(
      urlBuilder.NOTIFY_USER_ACTION(msisdn),
      new NotifyUserActionRequest(action, extra, televendasId),
      false
    )
  }
}

/**
 * request client otp
 *
 * @param {HttpClient} http
 * @param {String} cpf
 * @param {String} msisdn
 *
 * @returns {Promise<String>}
 */
const requestOtp = async (http, cpf, msisdn, isOfferSwitch) => {
  const canal = await getChannel()
  return http.post(
    urlBuilder.GENERATOR_OTP(cpf, msisdn),
    {},
    false,
    { trocaDePlano: isOfferSwitch, channel: canal }
  )
}

/**
 * Validate user and select plan
 *
 * @param {HttpClient} http
 * @param {String} cpf
 * @param {String} msisdn
 *
 * @returns {Promise<Object>}
 */
const validateUserAndSelectPlan = async (http, cpf, msisdn, planoId, isOfferSwitch, otp) => {
  const USER_ALREADY_HAS_AN_ACTIVE_OFFER = 'Adesão não permitida. Este cliente já está no Plano Claro Controle'
  try {
    const canal = await getChannel()
    return await http.post(
      otp ? urlBuilder.VALIDATE_USER_AND_SELECT_PLAN_WITH_OTP() : urlBuilder.VALIDATE_USER_AND_SELECT_PLAN(),
      { cpf, msisdn, planoId, otp },
      false,
      { trocaDePlano: isOfferSwitch, channel: canal }
    )
  } catch (err) {
    const responseBody = JSON.parse(err.metaData.body)
    if (responseBody.code === 'ValidarTokenCliente') throw new CpfDivergenteOtpGeradoException()
    if (responseBody.message !== USER_ALREADY_HAS_AN_ACTIVE_OFFER) throw err
  }
}

/**
 * check user and plan compatibility
 *
 * @param {HttpClient} http
 * @param {String} msisdn
 * @param {String} offerId
 *
 * @returns {Promise<AcaoPermitidaParaPlanoEscolhidoEnum>}
 */
const validateUserAndPlanCompatibility = (http, msisdn, offerId) => {
  return http.get(
    urlBuilder.VALIDATE_USER_AND_PLAN_COMPATIBILITY(msisdn, offerId)
  )
}

/**
 * @param {HttpClient} http
 * @param {String} cpf
 * @param {String} msisdn
 *
 * @returns {Promise<Array>}
 */
const validateUserProcess = async (http, cpf, msisdn, offerId) => {
  const unparsedResponse = await validateUserAndPlanCompatibility(http, msisdn, offerId)
  const userCompatibility = new ConfirmOTPResponse(unparsedResponse)
  let shouldValidateOtp = true
  if (!(userCompatibility.acaoPermitidaParaPlanoEscolhidoEnum === AcaoPermitidaParaPlanoEscolhidoEnum.NENHUMA)) {
    await authenticate(http)
  }
  if (userCompatibility.acaoPermitidaParaPlanoEscolhidoEnum === AcaoPermitidaParaPlanoEscolhidoEnum.TROCA) {
    await requestOtp(http, cpf, msisdn, true)
  }
  if (userCompatibility.acaoPermitidaParaPlanoEscolhidoEnum === AcaoPermitidaParaPlanoEscolhidoEnum.ADESAO) {
    try {
      await validateUserAndSelectPlan(http, cpf, msisdn, offerId, false, null)
      shouldValidateOtp = false
    } catch (e) {
      if (!(e instanceof CpfDivergenteOtpGeradoException)) throw e
    }
  }
  return { action: userCompatibility.acaoPermitidaParaPlanoEscolhidoEnum, shouldValidateOtp }
}

/**
 * @param {HttpClient} http
 * @param {String} offerId
 * @param {String} cpf
 * @param {String} msisdn
 *
 * @returns {Promise<Boolean>}
 */
const switchOfferTo = async (http, offerId, cpf, msisdn) => {
  return await http.post(
    urlBuilder.SWITCH_OFFER(cpf, msisdn, offerId),
    { },
    false
  )
}

/**
 * @param {HttpClient} http
 *
 * @returns {Promise<Boolean>}
 */
const authenticate = async (http) => {
  return await http.post(
    urlBuilder.AUTHENTICATE(),
    { },
    false
  )
}

const fetchCards = async (http, msisdn) => {
  if (msisdn) {
    const canal = await getChannel()
    return await http.get(
      urlBuilder.FETCH_CARDS(msisdn),
      { channel: canal }
    )
  }
}

const fetchUserCurrentPlan = async (http, msisdn) => {
  if (msisdn) {
    const canal = await getChannel()
    return await http.get(
      urlBuilder.FETCH_CURRENT_PLAN(msisdn),
      { channel: canal }
    )
  }
}

const validateBeauth = async (http, code) => {
  return await http.post(
    urlBuilder.VALIDATE_USER_BEAUTH(code)
  )
}

const actions = {
  requestOtp,
  validateUserAndSelectPlan,
  validateUserProcess,
  switchOfferTo,
  notifyUserAction,
  authenticate,
  fetchCards,
  fetchUserCurrentPlan,
  notifyUserActionWithTelevendasId,
  validateBeauth
}

const serviceBuilder = makeServiceBuilder(actions)

export {
  serviceBuilder,
  requestOtp,
  validateUserAndSelectPlan,
  validateUserProcess,
  switchOfferTo,
  notifyUserAction,
  authenticate,
  fetchCards,
  fetchUserCurrentPlan,
  notifyUserActionWithTelevendasId
}
